import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Dumbbell, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HANDLE = "the-built-by-discipline-tee";

const MeshIcon = ({ className }) => (
  <svg viewBox="0 0 40 40" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1.2" opacity="0.9">
      {[...Array(7)].map((_, i) => (
        <line key={`a${i}`} x1={-8 + i * 8} y1="0" x2={20 + i * 8} y2="40" />
      ))}
      {[...Array(7)].map((_, i) => (
        <line key={`b${i}`} x1={48 - i * 8} y1="0" x2={20 - i * 8} y2="40" />
      ))}
    </g>
  </svg>
);

const FEATURES = [
  { icon: "wolf", title: "RELENTLESS MINDSET", body: "For those who show up when it matters." },
  { icon: "mesh", title: "PREMIUM COMFORT", body: "Soft, durable, and built to move with you." },
  { icon: "dumbbell", title: "MADE TO WORK", body: "Designed for the grind. Every rep. Every day." },
  { icon: "globe", title: "WORLDWIDE SHIPPING", body: "Made on demand. Delivered to your door." },
];

const Icon = ({ type }) => {
  const cls = "w-9 h-9 text-white";
  if (type === "wolf") return <img src="/generated/wolf_white.png" alt="" aria-hidden="true" className="w-9 h-9 object-contain" />;
  if (type === "mesh") return <MeshIcon className={cls} />;
  if (type === "dumbbell") return <Dumbbell className={cls} strokeWidth={1.4} />;
  return <Globe className={cls} strokeWidth={1.2} />;
};

export const FeaturedProduct = () => {
  const navigate = useNavigate();
  const shop = () => navigate(`/product/${HANDLE}`);

  return (
    <section data-testid="featured-product" className="relative bg-[#050505] pt-24 md:pt-32 pb-4">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative border border-white/10 bg-[#070707] overflow-hidden"
        >
          {/* ambient spotlight */}
          <div className="absolute inset-0 pointer-events-none spotlight opacity-70" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 items-center gap-8 lg:gap-6 p-8 sm:p-10 lg:p-8">
            {/* LEFT — copy */}
            <div className="lg:col-span-4 lg:pl-6">
              <span className="font-sans text-[11px] tracking-[0.45em] text-white/45">FEATURED PRODUCT</span>
              <h2 className="mt-6 font-display font-black uppercase tracking-normal leading-[0.9] text-white text-5xl sm:text-6xl">
                The Built By<br />Discipline Tee
              </h2>
              <div className="mt-6 h-px w-14 bg-white/30" />
              <p className="mt-6 font-sans text-sm text-white/55 leading-relaxed max-w-xs">
                Built for the obsessed. Minimal in look, maximum in mindset. This is more than a tee — it's a standard.
              </p>
              <p className="mt-7 font-display font-bold text-3xl text-white">$34</p>
              <button
                data-testid="featured-shop-now"
                onClick={shop}
                className="group relative mt-6 inline-flex items-center gap-6 border border-white/25 pl-7 pr-6 py-4 overflow-hidden"
              >
                <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                <span className="relative font-sans text-xs tracking-[0.28em] text-white group-hover:text-black transition-colors duration-500">SHOP NOW</span>
                <ArrowRight className="relative w-4 h-4 text-white group-hover:text-black transition-all duration-500 group-hover:translate-x-1" />
              </button>
            </div>

            {/* CENTER — product */}
            <div className="lg:col-span-4 flex items-center justify-center">
              <button onClick={shop} className="w-full" aria-label="Shop The Built By Discipline Tee">
                <img
                  src="/generated/featured_tee.png"
                  alt="The Built By Discipline Tee"
                  className="w-full max-w-[440px] mx-auto object-contain transition-transform duration-700 hover:scale-[1.03]"
                />
              </button>
            </div>

            {/* RIGHT — features */}
            <div className="lg:col-span-4 lg:pr-6 space-y-8">
              {FEATURES.map((f) => (
                <div key={f.title} className="flex items-start gap-5">
                  <span className="shrink-0 mt-0.5 text-white/80">
                    <Icon type={f.icon} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold uppercase tracking-normal text-lg text-white leading-none">{f.title}</h3>
                    <p className="mt-2 font-sans text-sm text-white/50 leading-snug max-w-[22ch]">{f.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProduct;
